import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { SnackbarCloseReason } from "@mui/material";
import { Eye, EyeOff } from "lucide-react";
import axios from "axios";

import { useAuth } from "../../contexts/AuthContext";
import CustomSnackbar from "../../components/CustomSnackbar";
import petImage from "../../assets/Images/petimageadmin.png";

type SnackbarState = {
  open: boolean;
  message: string;
  severity: "success" | "error" | "info" | "warning";
};

const InputField = ({
  id,
  label,
  type = "text",
  value,
  placeholder,
  onChange,
  withToggle = false,
}: {
  id: string;
  label: string;
  type?: string;
  value: string;
  placeholder: string;
  onChange: (val: string) => void;
  withToggle?: boolean;
}) => {
  const [show, setShow] = useState(false);
  const inputType = withToggle ? (show ? "text" : "password") : type;

  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium mb-1">
        {label}
      </label>
      <div className="relative">
        <input
          id={id}
          type={inputType}
          value={value}
          placeholder={placeholder}
          autoComplete="off"
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-4 py-3 pr-12 rounded-lg focus:ring-[#f2445b] focus:border-[#f2445b] 
                     focus:outline-none shadow-md bg-white/10 text-white placeholder-white"
          required
        />
        {withToggle && (
          <button
            type="button"
            onClick={() => setShow((prev) => !prev)}
            className="absolute inset-y-0 right-3 flex items-center text-white"
          >
            {show ? <EyeOff size={20} /> : <Eye size={20} />}
          </button>
        )}
      </div>
    </div>
  );
};

const AdminLogin = () => {
  const { login } = useAuth();
  const navigate = useNavigate();

  const [credentials, setCredentials] = useState({ username: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: "",
    severity: "info",
  });

  const updateField = (field: "username" | "password", value: string) =>
    setCredentials((prev) => ({ ...prev, [field]: value }));

  const showSnackbar = (message: string, severity: SnackbarState["severity"]) =>
    setSnackbar({ open: true, message, severity });

  const wooLogin = async (username: string, password: string) => {
    const wooBaseUrl = process.env.REACT_APP_WOO_COMMERCE_BASE_URL;
    try {
      const { data } = await axios.post(
        `${wooBaseUrl}/wp-json/jwt-auth/v1/token`,
        { username, password }
      );
      sessionStorage.setItem("wooToken", data.token);
      sessionStorage.setItem("wooUser", JSON.stringify(data));
      return data;
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message;
      if (msg?.includes("username") || err.response?.data?.code === "invalid_username") {
        throw new Error("Username not found or invalid");
      }
      if (msg?.includes("password") || err.response?.data?.code === "incorrect_password") {
        throw new Error("Incorrect password");
      }
      throw new Error("WooCommerce authentication failed");
    }
  };


  const handleLogin = async () => {
    setLoading(true);
    try {
      await login(credentials.username, credentials.password);
      await wooLogin(credentials.username, credentials.password);
      showSnackbar("Login successful (Server + WooCommerce)!", "success");
      navigate("/dashboard", { replace: true });
    } catch (err: any) {
      console.error("Login failed:", err);
      showSnackbar(err.message || "Authentication failed", "error");
      setCredentials((prev) => ({ ...prev, password: "" }));
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = (
    _event: Event | React.SyntheticEvent<any, Event>,
    reason?: SnackbarCloseReason
  ) => {
    if (reason !== "clickaway") {
      setSnackbar((prev) => ({ ...prev, open: false }));
    }
  };

  return (
    <div className="relative w-full h-screen">
      <img
        src={petImage}
        alt="Pet Admin"
        className="absolute inset-0 object-cover w-full h-full"
      />
      <div className="absolute inset-0 bg-black bg-opacity-40" />
      <div className="absolute inset-0 flex items-center justify-center px-4">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleLogin();
          }}
          className="p-8 rounded-xl w-full max-w-md space-y-6 text-white"
        >
          <h2 className="text-3xl font-bold text-center mb-6">Admin Login</h2>

          <InputField
            id="username"
            label="Username"
            value={credentials.username}
            placeholder="Enter your username"
            onChange={(val) => updateField("username", val)}
          />

          <InputField
            id="password"
            label="Password"
            value={credentials.password}
            placeholder="Enter your password"
            onChange={(val) => updateField("password", val)}
            withToggle
          />

          <button
            type="submit"
            disabled={loading}
            className={`w-full py-3 bg-gradient-to-r from-[#f2445b] to-[#d7354f] 
                        text-white rounded-lg text-lg font-semibold 
                        hover:from-[#f2445b] hover:to-[#c02f44] 
                        transition duration-300 transform hover:scale-105 shadow-md 
                        ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            {loading ? "Signing In..." : "Sign In"}
          </button>
        </form>
      </div>

      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleCloseSnackbar}
        autoHideDuration={6000}
      />
    </div>
  );
};

export default AdminLogin;
